import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FilterService } from '@services/filter.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import {
  matchesShipmentStatusFilter,
  ShipmentStatus,
  ShipmentStatusFilterKey,
  ShipmentStatusFilterLabelMap,
  getShipmentStatusFilterKey,
} from '@components/shipment-list/shipment-status';

export type ShipmentDeviceViewModel = {
  /** デバイスID */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;
};

export type ShipmentSettingLocationViewModel = {
  /** 配送位置情報ID */
  locationId: string;
  /** 配送位置情報名 */
  locationName: string;
};

export type ShipmentSettingRouteViewModel = {
  /** 出発地 */
  startLocation?: ShipmentSettingLocationViewModel;
  /** 到着地 */
  arrivalLocation?: ShipmentSettingLocationViewModel;
};

export type ShipmentViewModel = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
  /** 繰り返し設定 */
  frequency?: '' | 'once' | 'repetition';
  /** 配送ステータス */
  status: string;
  /** 出発予定日時 */
  startDatetime: string;
  /** 出発実績日時 */
  actualStartDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** 到着実績日時 */
  actualArrivalDatetime?: string;
  /** センサーアラート取得開始時間(分数) */
  alertDetectionOffset?: number;
  /** ルート設定 */
  route: ShipmentSettingRouteViewModel;
  /** デバイス一覧 */
  deviceList: ShipmentDeviceViewModel[];
  /** 更新日時 */
  updatedAt?: string;
};

interface ShipmentListFilter {
  shipmentName?: string;
  startLocation?: string;
  arrivalLocation?: string;
  deviceName?: string;
  status?: ShipmentStatusFilterKey;
  sortState?: Sort;
  pageIndex?: number;
}

@Component({
  selector: 'app-shipment-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
  ],
  templateUrl: './shipment-list-table.component.html',
  styleUrl: './shipment-list-table.component.scss',
})
export class ShipmentListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** 配送設定一覧 */
  @Input({ required: true }) public shipmentList: ShipmentViewModel[] = [];
  /** マスターデータ取得中 */
  @Input() public loadingMasterData = false;

  /** 選択状態 */
  public selection = new SelectionModel<ShipmentViewModel>(true, []);

  /** 配送設定の新規登録を通知 */
  @Output() created = new EventEmitter<void>();
  /** 配送設定の更新を通知 */
  @Output() updated = new EventEmitter<ShipmentViewModel>();
  /** 配送設定の削除を通知 */
  @Output() deleted = new EventEmitter<ShipmentViewModel[]>();
  /** 配送設定の選択を通知 */
  @Output() selected = new EventEmitter<ShipmentViewModel>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = [
    'checkbox',
    'shipmentName',
    'startLocation',
    'arrivalLocation',
    'deviceName',
    'status',
    'repeatSetting',
    'updatedAt',
    'operation',
  ] as const;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<ShipmentViewModel>([]);
  /** 配送名検索キーワード */
  public shipmentNameKeyword = '';
  /** 出発地検索キーワード */
  public startLocationKeyword = '';
  /** 到着地検索キーワード */
  public arrivalLocationKeyword = '';
  /** デバイス名検索キーワード */
  public deviceNameKeyword = '';
  /** 配送ステータス選択 */
  public selectedShipmentStatus: ShipmentStatusFilterKey | undefined;
  /** 一覧内に存在する配送ステータスの重複なしリスト */
  public uniqueStatusList: ShipmentStatusFilterKey[] = [];
  /** 一括削除ボタン表示状態 */
  public isBulkDeleteButtonDisable = true;

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'shipmentName',
    direction: 'asc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;
  /** ページ番号 */
  private pageIndex: number | undefined;
  /** 配送ステータスの並び順 */
  private readonly shipmentStatusOrderList: Partial<
    Record<ShipmentStatus, number>
  > = {
    [ShipmentStatus.NotReady]: 0,
    [ShipmentStatus.Ready]: 1,
    [ShipmentStatus.InProgress]: 2,
    [ShipmentStatus.AtStop]: 3,
    [ShipmentStatus.AtDestination]: 4,
  };

  constructor(
    private filterService: FilterService<ShipmentListFilter, ShipmentViewModel>,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey('shipmentList');
  }

  public ngOnInit(): void {
    this.dataSource.data = this.shipmentList;
    this.dataSource.sort = this.sort;
    this.updateUniqueStatusList(this.shipmentList);
    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (
      item: ShipmentViewModel,
      property: string,
    ) => {
      if (property === 'startLocation') {
        return item.route.startLocation
          ? item.route.startLocation.locationName
          : '';
      }
      if (property === 'arrivalLocation') {
        return item.route.arrivalLocation
          ? item.route.arrivalLocation.locationName
          : '';
      }
      if (property === 'deviceName') {
        return item.deviceList[0] ? item.deviceList[0].deviceName : '';
      }
      if (property === 'repeatSetting') {
        return this.getRepeatSettingLabel(item.frequency);
      }
      if (property === 'status') {
        return item.status ? this.getShipmentStatusSortValue(item.status) : 999;
      }
      if (property === 'updatedAt') {
        return item.updatedAt ? new Date(item.updatedAt).getTime() : -1;
      }

      const value = item[property as keyof ShipmentViewModel];
      return value == null ? '' : `${value}`;
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルタ設定
    this.dataSource.filterPredicate = (element: ShipmentViewModel) => {
      let shipmentName = true;
      if (this.shipmentNameKeyword) {
        shipmentName =
          element.shipmentName
            .trim()
            .toLowerCase()
            .indexOf(this.shipmentNameKeyword.toLowerCase()) > -1;
      }
      let startLocation = true;
      if (this.startLocationKeyword) {
        startLocation =
          (element.route?.startLocation?.locationName ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.startLocationKeyword.toLowerCase()) > -1;
      }
      let arrivalLocation = true;
      if (this.arrivalLocationKeyword) {
        arrivalLocation =
          (element.route?.arrivalLocation?.locationName ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.arrivalLocationKeyword.toLowerCase()) > -1;
      }
      let deviceName = true;
      if (this.deviceNameKeyword) {
        deviceName = element.deviceList.some((device) => {
          return (
            ((device.deviceName || device.deviceId) ?? '')
              .trim()
              .toLowerCase()
              .indexOf(this.deviceNameKeyword.toLowerCase()) > -1
          );
        });
      }
      let status = true;
      if (this.selectedShipmentStatus) {
        status = matchesShipmentStatusFilter(
          this.selectedShipmentStatus,
          element.status,
        );
      }

      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        shipmentName && startLocation && arrivalLocation && deviceName && status
      );
    };
    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['shipmentList'] !== undefined) {
      const shipmentList: ShipmentViewModel[] =
        changes['shipmentList'].currentValue;
      this.dataSource.data = shipmentList;
      this.updateUniqueStatusList(shipmentList);
      // 配送設定一覧が更新された場合、選択情報をクリア
      this.selection.clear();
      // 一括削除ボタンの表示判定
      this.updateBulkDeleteButtonState();
    }
  }

  /** 一括削除ボタンの表示状態管理 */
  public updateBulkDeleteButtonState(): void {
    this.isBulkDeleteButtonDisable = this.selection.selected.length === 0;
  }

  /**
   * 一括削除ボタン押下処理
   */
  public bulkDelete() {
    this.deleted.emit(this.selection.selected);
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    this.dataSource.filter = 'dummy';
  }

  /** 配送名でのフィルタリングをクリアする。 */
  public clearShipmentNameFilter(): void {
    this.shipmentNameKeyword = '';
    this.applyFilter();
  }

  /** 出発地でのフィルタリングをクリアする。 */
  public clearStartLocationFilter(): void {
    this.startLocationKeyword = '';
    this.applyFilter();
  }

  /** 到着地でのフィルタリングをクリアする。 */
  public clearArrivalLocationFilter(): void {
    this.arrivalLocationKeyword = '';
    this.applyFilter();
  }

  /** デバイス名でのフィルタリングをクリアする。 */
  public clearDeviceNameFilter(): void {
    this.deviceNameKeyword = '';
    this.applyFilter();
  }

  /** 各フィルターを初期化する。 */
  public clearFilters() {
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    this.dataSource.filter = '';
    this.applyFilter();
  }

  /** ページネーションの番号を設定する */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /** フィルターをセッションストレージから読み込む。 */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.shipmentNameKeyword = filters.shipmentName || '';
    this.startLocationKeyword = filters.startLocation || '';
    this.arrivalLocationKeyword = filters.arrivalLocation || '';
    this.deviceNameKeyword = filters.deviceName || '';
    this.selectedShipmentStatus = filters.status;
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /** フィルター条件をセッションストレージに保存する */
  private saveFiltersToStorage(): void {
    const filterList: ShipmentListFilter = {
      shipmentName: this.shipmentNameKeyword,
      startLocation: this.startLocationKeyword,
      arrivalLocation: this.arrivalLocationKeyword,
      deviceName: this.deviceNameKeyword,
      status: this.selectedShipmentStatus,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }

  public updateUniqueStatusList(shipmentList: ShipmentViewModel[]) {
    this.uniqueStatusList = Array.from(
      new Set(
        shipmentList
          .map((shipment) => getShipmentStatusFilterKey(shipment.status))
          .filter((status) => !!status),
      ),
    ) as ShipmentStatusFilterKey[];
  }

  /**
   * デバイス名を取得する
   * @param deviceList デバイス一覧
   * @returns カンマでつなげたデバイス名
   */
  public getDeviceNames(deviceList: ShipmentDeviceViewModel[]): string {
    if (!deviceList) {
      return '削除済みデバイス';
    }
    const deviceNameList = deviceList.map(
      (device) => device.deviceName || device.deviceId,
    );
    return deviceNameList.join(',');
  }

  public getShipmentStatusFilterLabel(
    status: ShipmentStatusFilterKey | string,
  ): string {
    const filterKey =
      typeof status === 'string' &&
      Object.hasOwn(ShipmentStatusFilterLabelMap, status)
        ? (status as ShipmentStatusFilterKey)
        : getShipmentStatusFilterKey(status);
    return filterKey ? ShipmentStatusFilterLabelMap[filterKey] : '';
  }

  public getShipmentStatusLabel(status: string): string {
    return this.getShipmentStatusFilterLabel(getShipmentStatusFilterKey(status) ?? '');
  }

  public getShipmentStatusSortValue(status: string): number | string {
    if (status in this.shipmentStatusOrderList) {
      return (
        this.shipmentStatusOrderList[status as keyof typeof ShipmentStatus] ??
        999
      );
    }
    return 999;
  }

  public getRepeatSettingLabel(
    frequency?: ShipmentViewModel['frequency'],
  ): string {
    if (frequency === 'repetition') {
      return '繰り返し';
    }
    if (frequency === 'once') {
      return '一度のみ';
    }
    return '-';
  }
}
